<script setup lang="ts">
/**
 * 形状工作台页签 —— 按 **颜色 / 形状 / 大小** 把图元分组，再逐组统一形状。
 *
 * 交互约定（与色板同一套心智，用户已经熟悉）：
 *  · **悬停**某一行 = 画布上虚线框出这一组的元素（其余压暗），**不动选中集**
 *  · **点行** = 把这一组设为选中集（接着可以用右栏属性、Delete、对齐……）
 *  · **点行右侧图形** = 弹出形状浮层；**选形状立刻写入文档**，浮层保持打开可连点试
 *  · 一组 = 一条命令 = 一次 Ctrl+Z
 *
 * 为什么有「只看点状图元」这个开关：散点图里**同色的图例符号和置信椭圆会和点分到同一组**
 * （实测 plotly 的 PCoA 图：红组 = 18 个圆 + 1 个红椭圆），一键换形状会顺手毁掉椭圆轮廓。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { SHAPE_ICON_D, SHAPE_LIST, SHAPE_LABEL, type ShapeKind } from '@/core/shapes';
import type { GroupAxis, GroupScope } from '@/core/query/group';
import { setInspectorTab } from '@/stores/editor';
import { shapeState } from '@/stores/shapes';

const editor = useEditor();

const AXES: Array<{ axis: GroupAxis; label: string }> = [
  { axis: 'color', label: '颜色' },
  { axis: 'shape', label: '形状' },
  { axis: 'size', label: '大小' },
];

const SCOPES: Array<{ scope: GroupScope; label: string }> = [
  { scope: 'dotted', label: '只看点状图元' },
  { scope: 'all', label: '全部图形图元' },
];

/** 形状图标（16×16 描边路径；与属性面板的形状区共用同一份定义，见 core/shapes 的 SHAPE_ICON_D） */
const ICON_D = SHAPE_ICON_D;

const groups = computed(() => editor.shapeGroups.value);
const total = computed(() => groups.value.reduce((n, g) => n + g.count, 0));

/** 浮层位置（Teleport 到 body + fixed；右栏会滚动、会裁切，就地 absolute 会被切掉） */
const popAt = ref({ left: 0, top: 0 });
const POP_W = 268;
const POP_H = 210; // 估算高度：只为「下方放不下就向上弹」

const appliedOf = (key: string): ShapeKind | null => shapeState.applied[key] ?? null;
/** 行右侧现在是什么形状（模板里不做类型收窄，收窄放这里） */
const currentIcon = (key: string): string => {
  const kind = appliedOf(key);
  return kind ? ICON_D[kind] : '';
};
const pickTitle = (key: string): string => {
  const kind = appliedOf(key);
  return kind ? `现在是${SHAPE_LABEL[kind]}：点击换一个形状` : '点击选择形状';
};

const hover = (key: string | null): void => {
  shapeState.hover = key;
  editor.previewShapeGroup(key);
};

const selectRow = (key: string): void => {
  editor.selectShapeGroup(key);
};

const togglePicker = (key: string, e: MouseEvent): void => {
  if (shapeState.pickerFor === key) {
    shapeState.pickerFor = null;
    return;
  }
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const below = r.bottom + 6;
  popAt.value = {
    left: Math.max(8, Math.min(r.right - POP_W, window.innerWidth - POP_W - 8)),
    top: below + POP_H > window.innerHeight ? Math.max(8, r.top - POP_H - 6) : below,
  };
  shapeState.pickerFor = key;
};

/** 选形状：**立刻写入文档**（浮层不关 —— 连着点几个看画布变化才是正常用法）。
 *  提示文案一律由 `applyShapeGroup` 给（面板不写第二句话，否则会把更具体的那句盖掉）。 */
const pick = (kind: ShapeKind): void => {
  const key = shapeState.pickerFor;
  if (!key) return;
  editor.applyShapeGroup(key, kind);
};

const close = (): void => {
  shapeState.pickerFor = null;
  setInspectorTab('props');
};

/** 关闭时机：点外部 / Esc（捕获阶段拦下，别让编辑器的 Esc 顺手清掉选中） */
const onDocDown = (e: Event): void => {
  const t = e.target;
  if (t instanceof Element && (t.closest('.shp-pop') || t.closest('[data-field="shp-target"]'))) return;
  shapeState.pickerFor = null;
};
const onDocKey = (e: KeyboardEvent): void => {
  if (e.key !== 'Escape') return;
  e.stopPropagation();
  shapeState.pickerFor = null;
};

watch(
  () => shapeState.pickerFor,
  (open) => {
    if (open) {
      document.addEventListener('pointerdown', onDocDown, true);
      document.addEventListener('keydown', onDocKey, true);
    } else {
      document.removeEventListener('pointerdown', onDocDown, true);
      document.removeEventListener('keydown', onDocKey, true);
    }
  },
);
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown, true);
  document.removeEventListener('keydown', onDocKey, true);
  shapeState.hover = null;
  shapeState.pickerFor = null;
});

const iconFor = (kind: ShapeKind): string => ICON_D[kind];
/** 「无颜色」组没有色块：给它一个形状图标，免得左列空一块 */
const iconForGroup = (tag: string | null): ShapeKind => (tag === 'polygon' ? 'pentagon' : tag === 'ellipse' ? 'ellipse' : tag === 'rect' ? 'rect' : tag === 'line' ? 'plus' : 'circle');
</script>

<template>
  <div class="shp">
    <div class="shp-head">
      <span class="shp-icon" aria-hidden="true">⬟</span>
      <b>形状工作台</b>
      <span class="panel-note">· 从你的 SVG 自动分组</span>
      <span class="grow" />
      <span class="chip" data-field="shp-count">{{ groups.length }} 组 · {{ total }} 个元素</span>
      <button class="ghost-btn" title="关闭形状工作台" @click="close">✕</button>
    </div>

    <!-- 分组依据 + 范围：包成一个「控制条」盒子（否则一排控件直接贴面板边框上） -->
    <div class="shp-bar">
      <div class="shp-axis">
        <span class="shp-label">按</span>
        <div class="shp-seg">
          <button
            v-for="axis in AXES"
            :key="axis.axis"
            class="shp-seg-btn"
            :class="{ 'is-on': shapeState.axis === axis.axis }"
            data-field="shp-axis"
            :data-axis="axis.axis"
            @click="shapeState.axis = axis.axis"
          >
            {{ axis.label }}
          </button>
        </div>
      </div>

      <div class="shp-scope">
        <span class="shp-label">范围</span>
        <button
          v-for="item in SCOPES"
          :key="item.scope"
          class="shp-pill"
          :class="{ 'is-on': shapeState.scope === item.scope }"
          data-field="shp-scope"
          :data-scope="item.scope"
          :title="item.scope === 'dotted' ? '跨度 ≤ 图幅 5%：散点图里同色的图例符号与置信椭圆不会被算进来' : '文档里全部能换形状的图形图元'"
          @click="shapeState.scope = item.scope"
        >
          {{ item.label }}
        </button>
        <span class="grow" />
        <span class="panel-note">跨度 &lt; 图幅 5%</span>
      </div>
    </div>

    <div class="shp-list">
      <div
        v-for="g in groups"
        :key="g.key"
        class="shp-row"
        :class="{ 'is-hover': shapeState.hover === g.key, 'is-mapped': appliedOf(g.key) !== null }"
        data-field="shp-row"
        :data-key="g.key"
        :title="[g.label, `${g.count} 个元素`, g.parts, g.axis === 'size' ? '尺寸档：相邻尺寸差 ≤10% 归同一档' : ''].filter(Boolean).join(' · ')"
        @mouseenter="hover(g.key)"
        @mouseleave="hover(null)"
        @click="selectRow(g.key)"
      >
        <span v-if="g.swatch" class="shp-swatch" :style="{ background: g.swatch }" />
        <span v-else class="shp-swatch is-shape">
          <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round">
            <path :d="iconFor(iconForGroup(g.shapeTag))" />
          </svg>
        </span>
        <span class="shp-meta">
          <b>{{ g.label }}</b>
          <i>{{ g.count }} 个元素</i>
        </span>
        <button
          class="shp-pick"
          :class="{ 'is-open': shapeState.pickerFor === g.key }"
          data-field="shp-target"
          :data-key="g.key"
          :title="pickTitle(g.key)"
          @click.stop="togglePicker(g.key, $event)"
          @focus="hover(g.key)"
          @blur="hover(null)"
        >
          <span class="shp-arrow" aria-hidden="true">→</span>
          <span class="shp-slot" :class="{ 'is-empty': appliedOf(g.key) === null }">
            <svg
              v-if="appliedOf(g.key)"
              viewBox="0 0 16 16"
              width="17"
              height="17"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linejoin="round"
            >
              <path :d="currentIcon(g.key)" />
            </svg>
          </span>
        </button>
      </div>
      <p v-if="groups.length === 0" class="empty-hint" data-field="shp-empty">
        {{ editor.blank.value ? '画板还是空的 · 先打开 SVG' : '没有可换形状的图元（文本 / 图片 / 组不在内）' }}
      </p>
    </div>

    <p class="panel-note shp-hint" data-field="shp-hint">
      {{
        shapeState.hint ||
          '悬停行 → 画布上框出这一组（其余压暗）· 点行 → 选中这一组 · 点右侧图形 → 选形状（立刻写入，Ctrl+Z 可撤回）'
      }}
    </p>

    <!-- 形状浮层：Teleport 到 body（右栏会滚动会裁切 + 这一层故意能压住下面的行） -->
    <Teleport to="body">
      <div
        v-if="shapeState.pickerFor"
        class="shp-pop"
        data-field="shp-pop"
        :style="{ left: `${popAt.left}px`, top: `${popAt.top}px`, width: `${POP_W}px` }"
      >
        <div class="shp-pop-title">
          目标形状 ·
          {{ groups.find((g) => g.key === shapeState.pickerFor)?.label }}
          （{{ groups.find((g) => g.key === shapeState.pickerFor)?.count }} 个元素）
        </div>
        <div class="shp-grid">
          <button
            v-for="def in SHAPE_LIST"
            :key="def.kind"
            class="shp-shape"
            :class="{ 'is-active': appliedOf(shapeState.pickerFor) === def.kind }"
            data-field="shp-shape"
            :data-kind="def.kind"
            :title="def.label"
            @click="pick(def.kind)"
          >
            <svg viewBox="0 0 16 16" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round">
              <path :d="iconFor(def.kind)" />
            </svg>
          </button>
        </div>
        <div class="shp-div" />
        <div class="shp-fit">
          <span>尺寸口径</span>
          <span class="shp-fit-labels">
            <button
              class="shp-fit-btn"
              :class="{ 'is-on': shapeState.fit === 'bbox' }"
              data-field="shp-fit"
              data-fit="bbox"
              title="内接在原来的包围盒里，中心不动（matplotlib 定义 marker 的口径）"
              @click="shapeState.fit = 'bbox'"
            >
              保持包围盒
            </button>
            <button
              class="shp-fit-btn"
              :class="{ 'is-on': shapeState.fit === 'area' }"
              data-field="shp-fit"
              data-fit="area"
              title="等比缩放到与原来面积相等（气泡图那种用大小编码变量的图更合适）"
              @click="shapeState.fit = 'area'"
            >
              保持面积
            </button>
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>
